import { Portal } from "@headlessui/react";
import { ReactNode, useState } from "react";
import { MdWarningAmber } from "react-icons/md";

type AlertTooltipType = "warning" | "error";

const typeStyles: Record<AlertTooltipType, { iconColor: string; ring: string }> = {
  warning: { iconColor: "#FFA726", ring: "ring-amber-100" },
  error: { iconColor: "#DC2626", ring: "ring-red-100" },
};

interface AlertTooltipProps {
  message: string;
  type?: AlertTooltipType;
  size?: number;
  children?: ReactNode;
  className?: string;
}

const AlertTooltip = ({
  message,
  type = "warning",
  size = 18,
  children,
  className = "",
}: AlertTooltipProps) => {
  const [panelPosition, setPanelPosition] = useState<{ top: number; left: number } | null>(null);
  const styles = typeStyles[type];

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setPanelPosition({ top: rect.bottom + 4, left: Math.max(8, rect.left) });
      }}
      onMouseLeave={() => setPanelPosition(null)}
    >
      <span
        role="button"
        tabIndex={0}
        aria-label={message}
        data-stop-row-click="true"
        className={`flex items-center gap-1 ${className}`}
      >
        {children ?? <MdWarningAmber color={styles.iconColor} size={size} />}
      </span>
      {panelPosition && (
        <Portal>
          <div
            className={`fixed z-[9999] max-w-xs rounded-lg bg-white p-3 text-left text-xs font-medium text-gray-700 shadow-lg ring-1 ${styles.ring}`}
            style={{ top: panelPosition.top, left: panelPosition.left }}
          >
            {message}
          </div>
        </Portal>
      )}
    </div>
  );
};

export default AlertTooltip;
